import { useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import { adminService, type ContentWritePayload } from '../../services/adminService'
import type { ContentItem } from '../../data/types'
import { EmptyState, LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import TargetingSelect from './TargetingSelect'
import AssetUploadButton from '../AssetUploadButton'
import { assetIdFromUrl } from '../assetUrl'

/** Human-readable size label from a byte count (e.g. "2.4 MB"). */
function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  const kb = bytes / 1024
  if (kb < 1024) return `${Math.round(kb)} KB`
  return `${(kb / 1024).toFixed(1)} MB`
}

/** Content kinds whose files our object store accepts (PDF-only among docs). */
const UPLOADABLE_KINDS = new Set(['pdf', 'slides'])

const KINDS = ['pdf', 'slides', 'video', 'link']

export default function ContentManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [items, setItems] = useState<ContentItem[]>([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState<ContentItem | null>(null)
  const [open, setOpen] = useState(false)

  const load = () =>
    adminService
      .listContent(eventId)
      .then(setItems)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const remove = async (i: ContentItem) => {
    if (!(await confirmDelete(`Delete "${i.title}"?`))) return
    try {
      await adminService.deleteContent(eventId, i.id)
      // Clean up the backing object if this item's URL was one of ours.
      // Best-effort: ignore failures (e.g. already gone / external URL) so a
      // storage hiccup never blocks the delete the user asked for.
      const assetId = assetIdFromUrl(i.url)
      if (assetId) {
        await adminService.deleteAsset(eventId, assetId).catch(() => undefined)
      }
      notify('Content deleted')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddRoundedIcon />}
          onClick={() => {
            setEditing(null)
            setOpen(true)
          }}
        >
          Add content
        </Button>
      </Stack>

      {items.length === 0 ? (
        <EmptyState title="No content yet" description="Add briefings, decks, and resources." />
      ) : (
        <Stack spacing={1.5}>
          {items.map((i) => (
            <Card
              key={i.id}
              variant="outlined"
              sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2, borderRadius: 3 }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="subtitle1" fontWeight={700} noWrap>
                    {i.title}
                  </Typography>
                  <Chip label={i.kind} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                </Stack>
                <Typography variant="body2" color="text.secondary" noWrap>
                  {[i.category, i.description].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => {
                  setEditing(i)
                  setOpen(true)
                }}
              >
                <EditRoundedIcon />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => remove(i)}>
                <DeleteRoundedIcon />
              </IconButton>
            </Card>
          ))}
        </Stack>
      )}

      {open && (
        <ContentDialog
          eventId={eventId}
          item={editing}
          onClose={() => setOpen(false)}
          onSaved={() => {
            setOpen(false)
            load()
          }}
        />
      )}
    </Box>
  )
}

function ContentDialog({
  eventId,
  item,
  onClose,
  onSaved,
}: {
  eventId: string
  item: ContentItem | null
  onClose: () => void
  onSaved: () => void
}) {
  const { notify } = useToast()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: item?.title ?? '',
    description: item?.description ?? '',
    category: item?.category ?? '',
    kind: item?.kind ?? 'pdf',
    url: item?.url ?? '',
    sizeLabel: item?.sizeLabel ?? '',
    availableFrom: item?.availableFrom ?? '',
  })
  const [targetingRuleId, setTargetingRuleId] = useState<string | null>(
    item?.targetingRuleId ?? null,
  )
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }))

  // Asset ids uploaded during this dialog session, so we can clean up ones that
  // never get persisted: superseded by a later upload, or abandoned on Cancel.
  const uploadedIds = useRef<string[]>([])

  const deleteAssets = (ids: string[]) =>
    Promise.all(
      // Best-effort; ignore failures so cleanup never surfaces an error.
      ids.map((id) => adminService.deleteAsset(eventId, id).catch(() => undefined)),
    )

  const onUploaded = (url: string, meta: { id: string; byteSize: number }) => {
    // A previous in-dialog upload is now orphaned — drop it (unless it's the
    // item's already-persisted asset, which save/replace handles).
    const prev = assetIdFromUrl(form.url)
    if (prev && prev !== assetIdFromUrl(item?.url) && prev !== meta.id) {
      void deleteAssets([prev])
      uploadedIds.current = uploadedIds.current.filter((id) => id !== prev)
    }
    uploadedIds.current.push(meta.id)
    set('url', url)
    // Auto-fill the size label from the uploaded file (unless the user already
    // typed one).
    if (!form.sizeLabel) set('sizeLabel', formatBytes(meta.byteSize))
  }

  // On Cancel / dismiss, clean up any uploads that weren't saved onto the item.
  const cancel = () => {
    const persisted = assetIdFromUrl(item?.url)
    const orphans = uploadedIds.current.filter((id) => id !== persisted)
    if (orphans.length) void deleteAssets(orphans)
    onClose()
  }

  const save = async () => {
    if (!form.title) {
      notify('Title is required', 'error')
      return
    }
    const payload: ContentWritePayload = {
      title: form.title,
      description: form.description || undefined,
      category: form.category || undefined,
      kind: form.kind,
      url: form.url || undefined,
      sizeLabel: form.sizeLabel || undefined,
      availableFrom: form.availableFrom || undefined,
      targetingRuleId,
    }
    setSaving(true)
    try {
      if (item) await adminService.updateContent(eventId, item.id, payload)
      else await adminService.createContent(eventId, payload)

      // Clean up now-orphaned objects, best-effort, after the save succeeds so a
      // storage error never blocks it. Two sources of orphans:
      //   1. The item's previously-persisted asset, if editing replaced/cleared it.
      //   2. Any in-dialog uploads that aren't the one we just saved (superseded
      //      by a later upload before Save).
      const savedId = assetIdFromUrl(form.url)
      const orphans = new Set<string>()
      const prevPersisted = assetIdFromUrl(item?.url)
      if (prevPersisted && prevPersisted !== savedId) orphans.add(prevPersisted)
      for (const id of uploadedIds.current) {
        if (id !== savedId) orphans.add(id)
      }
      uploadedIds.current = []
      if (orphans.size) await deleteAssets([...orphans])

      notify(item ? 'Content updated' : 'Content added')
      onSaved()
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={cancel} maxWidth="sm" fullWidth>
      <DialogTitle>{item ? 'Edit content' : 'Add content'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Title"
            required
            fullWidth
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
          />
          <TextField
            label="Description"
            fullWidth
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
          <TextField
            label="Category"
            fullWidth
            placeholder="Event Essentials"
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
          />
          <TextField
            label="Type"
            select
            fullWidth
            value={form.kind}
            onChange={(e) => set('kind', e.target.value)}
          >
            {KINDS.map((k) => (
              <MenuItem key={k} value={k} sx={{ textTransform: 'capitalize' }}>
                {k}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              label={form.kind === 'link' ? 'URL' : 'URL (optional)'}
              fullWidth
              value={form.url}
              onChange={(e) => set('url', e.target.value)}
            />
            {UPLOADABLE_KINDS.has(form.kind) && (
              <AssetUploadButton
                eventId={eventId}
                accept="application/pdf"
                onUploaded={onUploaded}
              />
            )}
          </Stack>
          <TextField
            label="Size / duration label"
            fullWidth
            placeholder="2.4 MB"
            value={form.sizeLabel}
            onChange={(e) => set('sizeLabel', e.target.value)}
          />
          <TextField
            label="Availability note"
            fullWidth
            placeholder="Available after the session"
            value={form.availableFrom}
            onChange={(e) => set('availableFrom', e.target.value)}
          />
          <TargetingSelect
            eventId={eventId}
            value={targetingRuleId}
            onChange={setTargetingRuleId}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={cancel} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
